import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { hostUI } from '../../../../configuraciones';
import { TIPOS_DOCUMENTO_RANGO } from './ModalDocumentosRangoHolograma';

import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  const num = Number(bytes);
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? String(dateStr) : d.toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch (_) {
    return String(dateStr);
  }
};

const ModalDocumentosSalidaHolograma = ({
  isOpen,
  onClose,
  salida,
  rango = null,
  lote = null,
  salidasDelRango = [],
  onDocumentUpdated
}) => {
  const [selectedSalida, setSelectedSalida] = useState(salida || null);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [activeTab, setActiveTab] = useState('listado'); // 'listado' | 'nuevo'

  const [tipoDocumento, setTipoDocumento] = useState('certificado_producto_envasado');
  const [nombreDocumento, setNombreDocumento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (salida) {
        setSelectedSalida(salida);
      } else if (Array.isArray(salidasDelRango) && salidasDelRango.length > 0) {
        setSelectedSalida(salidasDelRango[0]);
      }
    }
  }, [isOpen, salida, salidasDelRango]);

  const idSalida = Number(selectedSalida?.id_salida || selectedSalida?.id);
  const esSalidaPersistida = Number.isInteger(idSalida) && idSalida > 0;

  const prefijo = selectedSalida?.prefijo || rango?.prefijo || 'A';
  const etiquetaSalida = selectedSalida
    ? (selectedSalida.rango_completo || selectedSalida.rangoHologramas || `${prefijo}${selectedSalida.rango1 || selectedSalida.rango_inicial || ''} - ${prefijo}${selectedSalida.rango2 || selectedSalida.rango_final || ''}`)
    : 'Sin salida seleccionada';
  const tipoSalidaLabel = selectedSalida?.tipo_salida || selectedSalida?.tipoSalida || 'Salida de producto terminado';
  const etiquetaRangoPadre = rango
    ? (rango.rangoHologramas || rango.rango_completo || `${rango.prefijo || 'A'}${rango.rango1 || rango.rango_inicial || ''} - ${rango.prefijo || 'A'}${rango.rango2 || rango.rango_final || ''}`)
    : null;

  const fetchDocumentos = async () => {
    if (!esSalidaPersistida) {
      setDocumentos([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${hostUI}bitacora-envasado/producto/salida/${idSalida}/documentos`);
      if (res.data?.data) {
        setDocumentos(res.data.data);
      } else {
        setDocumentos([]);
      }
    } catch (error) {
      console.error('Error al obtener documentos de la salida de hologramas:', error);
      setDocumentos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && esSalidaPersistida) {
      fetchDocumentos();
      setActiveTab('listado');
      resetFormulario();
    }
  }, [isOpen, idSalida]);

  const resetFormulario = () => {
    setTipoDocumento('certificado_producto_envasado');
    setNombreDocumento('');
    setObservaciones('');
    setArchivosSeleccionados([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const validFiles = [];

    for (const file of files) {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExts.includes(ext)) {
        Swal.fire({
          icon: 'warning',
          title: 'Formato no válido',
          text: `El archivo "${file.name}" no es válido. Solo se permiten PDF o imágenes (JPG, PNG, WEBP).`,
          confirmButtonColor: '#0284C7'
        });
        continue;
      }
      if (file.size > 30 * 1024 * 1024) {
        Swal.fire({
          icon: 'warning',
          title: 'Archivo demasiado grande',
          text: `El archivo "${file.name}" supera el límite de 30 MB.`,
          confirmButtonColor: '#0284C7'
        });
        continue;
      }
      validFiles.push(file);
    }

    setArchivosSeleccionados((prev) => [...prev, ...validFiles]);
  };

  const handleRemoveSelectedFile = (idx) => {
    setArchivosSeleccionados((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubirDocumento = async (e) => {
    e.preventDefault();
    if (!esSalidaPersistida) {
      Swal.fire({
        icon: 'info',
        title: 'Salida aún no guardada',
        text: 'Primero guarde las salidas del rango para subir documentos al expediente en el servidor.',
        confirmButtonColor: '#0284C7'
      });
      return;
    }

    if (archivosSeleccionados.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo requerido',
        text: 'Por favor selecciona al menos un archivo PDF o imagen para adjuntar a esta salida.',
        confirmButtonColor: '#0284C7'
      });
      return;
    }

    if (tipoDocumento === 'otro' && !nombreDocumento.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nombre requerido',
        text: 'Por favor especifica el nombre o descripción para "Otro Documento".',
        confirmButtonColor: '#0284C7'
      });
      return;
    }

    const tipoObj = TIPOS_DOCUMENTO_RANGO.find((t) => t.id === tipoDocumento);
    const nombreFinalBase = tipoDocumento === 'otro'
      ? nombreDocumento.trim()
      : (tipoObj?.nombre || 'Certificado de Producto Envasado');

    setSubiendo(true);
    try {
      for (let i = 0; i < archivosSeleccionados.length; i++) {
        const file = archivosSeleccionados[i];
        const formData = new FormData();
        formData.append('archivo', file);
        formData.append('tipo_documento', tipoDocumento);
        const nombreDoc = archivosSeleccionados.length > 1 && tipoDocumento === 'otro'
          ? `${nombreFinalBase} (${i + 1})`
          : nombreFinalBase;
        formData.append('nombre_documento', nombreDoc);
        if (observaciones.trim()) {
          formData.append('observaciones', observaciones.trim());
        }

        await axios.post(
          `${hostUI}bitacora-envasado/producto/salida/${idSalida}/documentos`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      Swal.fire({
        icon: 'success',
        title: archivosSeleccionados.length > 1 ? '¡Documentos de salida adjuntados!' : '¡Documento de salida adjuntado!',
        text: `Se ${archivosSeleccionados.length > 1 ? `guardaron ${archivosSeleccionados.length} documentos` : `guardó "${nombreFinalBase}"`} en el expediente de la salida ${etiquetaSalida}.`,
        timer: 2000,
        showConfirmButton: false
      });

      resetFormulario();
      setActiveTab('listado');
      await fetchDocumentos();
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (error) {
      console.error('Error al subir documento de la salida:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al subir documento',
        text: error.response?.data?.message || 'Ocurrió un problema al guardar el archivo.',
        confirmButtonColor: '#DC2626'
      });
    } finally {
      setSubiendo(false);
    }
  };

  const handleVerDocumento = (doc) => {
    const url = `${hostUI}bitacora-envasado/producto/salida/documentos/${doc.id_documento}/ver`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleEliminarDocumento = async (doc) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar documento de la salida?',
      html: `Se eliminará permanentemente <b>${doc.nombre_documento}</b> (${doc.archivo_nombre_original || doc.nombre_original || 'archivo'}) de la salida <b>${etiquetaSalida}</b>.`,
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${hostUI}bitacora-envasado/producto/salida/documentos/${doc.id_documento}`);
      Swal.fire({
        icon: 'success',
        title: 'Eliminado',
        text: 'El documento fue eliminado del expediente de la salida.',
        timer: 1600,
        showConfirmButton: false
      });
      await fetchDocumentos();
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (error) {
      console.error('Error al eliminar documento de la salida:', error);
      Swal.fire({
        icon: 'error',
        title: 'No se pudo eliminar',
        text: error.response?.data?.message || 'Error al eliminar el documento.',
        confirmButtonColor: '#DC2626'
      });
    }
  };

  const getBadgeForTipo = (tipo) => {
    const found = TIPOS_DOCUMENTO_RANGO.find((t) => t.id === tipo) || TIPOS_DOCUMENTO_RANGO[2];
    const IconComp = found.icon;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '4px 10px',
          borderRadius: '999px',
          fontSize: '11px',
          fontWeight: 700,
          backgroundColor: found.bg,
          color: found.color,
          border: `1px solid ${found.border}`
        }}
      >
        <IconComp style={{ fontSize: 13 }} />
        {found.nombre}
      </span>
    );
  };

  const conteoPorTipo = TIPOS_DOCUMENTO_RANGO.reduce((acc, t) => {
    acc[t.id] = documentos.filter((d) => d.tipo_documento === t.id).length;
    return acc;
  }, {});

  const listaSalidas = Array.isArray(salidasDelRango) && salidasDelRango.length > 0
    ? salidasDelRango
    : (selectedSalida ? [selectedSalida] : []);

  const modalContent = (
    <div className="modal-backdrop-custom animate-fadeIn" style={{ zIndex: 1100010 }} onClick={onClose}>
      <div
        className="detail-modal-card animate-fadeIn"
        style={{
          maxWidth: '880px',
          width: '95%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER — Azul Cian para diferenciarlo claramente del expediente morado del Rango */}
        <div
          className="detail-modal-header"
          style={{
            background: 'linear-gradient(135deg, #075985 0%, #0284C7 60%, #0EA5E9 100%)',
            color: '#fff',
            padding: '18px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.12)'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'rgba(255,255,255,0.22)',
                  color: '#E0F2FE',
                  padding: '3px 10px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '0.03em'
                }}
              >
                🚚 EXPEDIENTE EXCLUSIVO DE LA SALIDA
              </span>
              {etiquetaRangoPadre && (
                <span
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}
                >
                  Rango Padre: {etiquetaRangoPadre}
                </span>
              )}
              {(lote?.loteEnvasado || lote?.lote_envasado) && (
                <span
                  style={{
                    background: 'rgba(255,255,255,0.14)',
                    color: '#fff',
                    padding: '3px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 600
                  }}
                >
                  Lote: {lote.loteEnvasado || lote.lote_envasado} {lote.marca ? `— ${lote.marca}` : ''}
                </span>
              )}
            </div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LocalShippingIcon style={{ fontSize: 20 }} />
              <span>Documentos de la Salida: {etiquetaSalida} ({tipoSalidaLabel})</span>
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.88)' }}>
              Adjunta el Certificado de Producto Envasado, Informe de Servicio u otros documentos específicos de esta salida.
            </p>
          </div>
          <button
            className="btn-close-modal"
            onClick={onClose}
            style={{ color: '#fff', background: 'rgba(255,255,255,0.15)' }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* SELECTOR SI EL RANGO TIENE VARIAS SALIDAS */}
        {listaSalidas.length > 1 && (
          <div
            style={{
              padding: '10px 24px',
              background: '#F0F9FF',
              borderBottom: '1px solid #BAE6FD',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#0369A1' }}>
              Seleccionar Salida del Rango:
            </span>
            {listaSalidas.map((sal, idx) => {
              const salId = sal.id_salida || sal.id;
              const isSelected = String(salId) === String(idSalida);
              const txt = sal.rango_completo || sal.rangoHologramas || `${sal.prefijo || prefijo}${sal.rango1 || sal.rango_inicial} - ${sal.prefijo || prefijo}${sal.rango2 || sal.rango_final}`;
              const docsCount = Array.isArray(sal.documentos) ? sal.documentos.length : (sal.total_documentos || 0);
              return (
                <button
                  key={salId || idx}
                  type="button"
                  onClick={() => setSelectedSalida(sal)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #0284C7' : '1px solid #7DD3FC',
                    background: isSelected ? '#0284C7' : '#fff',
                    color: isSelected ? '#fff' : '#0369A1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>Salida #{idx + 1}: {txt} ({sal.tipo_salida || 'Sin tipo'})</span>
                  <span
                    style={{
                      background: isSelected ? 'rgba(255,255,255,0.25)' : '#E0F2FE',
                      color: isSelected ? '#fff' : '#0369A1',
                      padding: '1px 6px',
                      borderRadius: '999px',
                      fontSize: '10px'
                    }}
                  >
                    {docsCount} doc{docsCount === 1 ? '' : 's'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* BARRA DE PESTAÑAS */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 24px',
            backgroundColor: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('listado')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: activeTab === 'listado' ? '1.5px solid #0284C7' : '1px solid #CBD5E1',
                backgroundColor: activeTab === 'listado' ? '#F0F9FF' : '#fff',
                color: activeTab === 'listado' ? '#0369A1' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <FolderSharedIcon style={{ fontSize: 16 }} />
              Documentos de la Salida ({documentos.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('nuevo')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: activeTab === 'nuevo' ? '1.5px solid #0284C7' : '1px solid #CBD5E1',
                backgroundColor: activeTab === 'nuevo' ? '#0284C7' : '#fff',
                color: activeTab === 'nuevo' ? '#fff' : '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <AddCircleOutlineIcon style={{ fontSize: 16 }} />
              Adjuntar Documento a la Salida
            </button>
          </div>

          <button
            type="button"
            onClick={fetchDocumentos}
            disabled={loading}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#fff',
              color: '#475569',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <RefreshIcon style={{ fontSize: 15 }} />
            Actualizar
          </button>
        </div>

        {/* BODY */}
        <div
          className="detail-modal-body"
          style={{
            padding: '20px 24px',
            overflowY: 'auto',
            flex: 1,
            backgroundColor: '#fff'
          }}
        >
          {/* Tarjetas resumen por categoría */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
              gap: '10px',
              marginBottom: '18px'
            }}
          >
            {TIPOS_DOCUMENTO_RANGO.map((t) => {
              const IconComp = t.icon;
              const count = conteoPorTipo[t.id] || 0;
              return (
                <div
                  key={t.id}
                  onClick={() => {
                    setTipoDocumento(t.id);
                    setActiveTab('nuevo');
                  }}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: `1.5px solid ${count > 0 ? t.border : '#E2E8F0'}`,
                    backgroundColor: count > 0 ? t.bg : '#F8FAFC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  title={`Clic para adjuntar ${t.nombre}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: t.color,
                        border: `1px solid ${t.border}`
                      }}
                    >
                      <IconComp style={{ fontSize: 18 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                        {t.nombre}
                      </div>
                      <div style={{ fontSize: '10.5px', color: count > 0 ? t.color : '#64748B', fontWeight: 600 }}>
                        {count > 0 ? `${count} archivo(s)` : 'Sin adjuntar'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {activeTab === 'listado' ? (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748B' }}>
                  Cargando documentos de la salida {etiquetaSalida}...
                </div>
              ) : documentos.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '36px 20px',
                    borderRadius: '12px',
                    border: '2px dashed #BAE6FD',
                    backgroundColor: '#F0F9FF'
                  }}
                >
                  <UploadFileIcon style={{ fontSize: 44, color: '#0284C7', marginBottom: 8 }} />
                  <h4 style={{ margin: '0 0 6px', color: '#0C4A6E', fontSize: '15px', fontWeight: 700 }}>
                    Esta salida aún no tiene documentos cargados
                  </h4>
                  <p style={{ margin: '0 0 16px', color: '#475569', fontSize: '13px' }}>
                    Puedes adjuntar el <b>Certificado de Producto Envasado</b>, el <b>Informe de Servicio</b> u otros documentos exclusivos de esta salida.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('nuevo')}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#0284C7',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <AddCircleOutlineIcon style={{ fontSize: 17 }} />
                    Adjuntar Primer Documento a la Salida
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {documentos.map((doc) => (
                    <div
                      key={doc.id_documento}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: '240px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          {getBadgeForTipo(doc.tipo_documento)}
                          <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#0F172A' }}>
                            {doc.nombre_documento}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span>📎 {doc.archivo_nombre_original || doc.nombre_original || doc.nombre_archivo}</span>
                          {doc.tamano_bytes ? <span>• {formatFileSize(doc.tamano_bytes)}</span> : null}
                          {doc.createdAt ? <span>• Subido: {formatDate(doc.createdAt)}</span> : null}
                        </div>
                        {doc.observaciones && (
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#475569', fontStyle: 'italic' }}>
                            "{doc.observaciones}"
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleVerDocumento(doc)}
                          style={{
                            padding: '7px 13px',
                            borderRadius: '8px',
                            border: '1px solid #7DD3FC',
                            backgroundColor: '#F0F9FF',
                            color: '#0369A1',
                            fontWeight: 700,
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <VisibilityIcon style={{ fontSize: 15 }} />
                          Ver / Descargar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEliminarDocumento(doc)}
                          style={{
                            padding: '7px 10px',
                            borderRadius: '8px',
                            border: '1px solid #FECACA',
                            backgroundColor: '#FEF2F2',
                            color: '#DC2626',
                            fontWeight: 700,
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Eliminar documento"
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubirDocumento} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '8px' }}>
                  1. Selecciona el Tipo de Documento de la Salida *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px' }}>
                  {TIPOS_DOCUMENTO_RANGO.map((t) => {
                    const IconComp = t.icon;
                    const selected = tipoDocumento === t.id;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setTipoDocumento(t.id)}
                        style={{
                          padding: '12px',
                          borderRadius: '10px',
                          border: selected ? `2px solid ${t.color}` : '1px solid #CBD5E1',
                          backgroundColor: selected ? t.bg : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <IconComp style={{ fontSize: 18, color: t.color }} />
                          <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#0F172A' }}>
                            {t.nombre}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748B', lineHeight: 1.35 }}>
                          {t.desc}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {tipoDocumento === 'otro' && (
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>
                    Nombre del Documento *
                  </label>
                  <input
                    type="text"
                    value={nombreDocumento}
                    onChange={(e) => setNombreDocumento(e.target.value)}
                    placeholder="Ej. Pedimento de Exportación, Guía de Traslado, Factura, etc."
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #CBD5E1',
                      fontSize: '13px'
                    }}
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>
                  2. Selecciona Archivo(s) (PDF, JPG, PNG — Máx. 30 MB) *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  multiple
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1.5px dashed #0284C7',
                    backgroundColor: '#F0F9FF',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                />
                {archivosSeleccionados.length > 0 && (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {archivosSeleccionados.map((f, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 10px',
                          backgroundColor: '#E0F2FE',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#0C4A6E'
                        }}
                      >
                        <span>📄 {f.name} ({formatFileSize(f.size)})</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedFile(i)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#DC2626',
                            cursor: 'pointer',
                            fontWeight: 700
                          }}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#1E293B', marginBottom: '6px' }}>
                  3. Observaciones (Opcional)
                </label>
                <textarea
                  rows={2}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas adicionales sobre el documento de esta salida..."
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    resetFormulario();
                    setActiveTab('listado');
                  }}
                  style={{
                    padding: '9px 16px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    backgroundColor: '#fff',
                    color: '#475569',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={subiendo}
                  style={{
                    padding: '9px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0369A1 0%, #0284C7 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: subiendo ? 'not-allowed' : 'pointer',
                    opacity: subiendo ? 0.7 : 1
                  }}
                >
                  {subiendo ? 'Subiendo archivo(s)...' : 'Guardar en Expediente de la Salida'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* FOOTER */}
        <div
          className="detail-modal-footer"
          style={{
            padding: '12px 24px',
            backgroundColor: '#F8FAFC',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ fontSize: '12px', color: '#64748B' }}>
            Total de documentos en esta salida: <b>{documentos.length}</b>
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 18px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#64748B',
              color: '#fff',
              fontWeight: 700,
              fontSize: '12.5px',
              cursor: 'pointer'
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};

export default ModalDocumentosSalidaHolograma;

